import { ITEM_TYPE_RESPONDER } from '@client/components/Editor/settings'

/**
 * Why a save was rejected, as far as the user is concerned (FE-1431).
 *
 * Only VALIDATION can be fixed by editing. The others need something done
 * outside the editor, so autosave stops for them instead of sending the same
 * rejected request every 60 seconds.
 */
export const SAVE_ERROR_KINDS = {
  // 400: the body fails backend validation, e.g. a survey choice question
  // without options, or an empty form URL whitelist.
  VALIDATION: 'validation',
  // 403 on an autoresponder: it is running, and only paused ones can be saved.
  AUTORESPONDER_RUNNING: 'autoresponder_running',
  // 403 on a landing page or survey below the Standard plan with no trial.
  PLAN: 'plan',
  // 404: the item was deleted, or a newsletter was scheduled, sent or put in
  // review, which takes it out of the drafts the save endpoint accepts.
  GONE: 'gone',
  OTHER: 'other',
}

// The backend's plan permission message: "You need to upgrade to Standard in
// order to access this endpoint."
const PLAN_DETAIL_PATTERN = /upgrade/i

const humanize = (key) => {
  const words = String(key).replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Flattens a DRF error body into readable lines. Field errors are a nested
 * dict (`{"questions": [{}, {"choices": ["…"]}]}`), anything else is
 * `{"detail": "…"}`. List positions are shown 1-based next to their field:
 * "Questions 2 › Choices: At least one choice required for choice field".
 */
export const flattenErrorMessages = (data) => {
  const lines = []

  const walk = (value, path) => {
    if (value === null || value === undefined) {
      return
    }
    if (typeof value === 'string') {
      lines.push(path.length ? `${path.join(' › ')}: ${value}` : value)
      return
    }
    if (Array.isArray(value)) {
      if (value.every((item) => typeof item === 'string')) {
        value.forEach((item) => walk(item, path))
        return
      }
      value.forEach((item, index) => {
        if (!path.length) {
          walk(item, [`${index + 1}`])
          return
        }
        const indexed = [...path]
        indexed[indexed.length - 1] = `${indexed[indexed.length - 1]} ${
          index + 1
        }`
        walk(item, indexed)
      })
      return
    }
    if (typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        const isGeneral = key === 'detail' || key === 'non_field_errors'
        walk(value[key], isGeneral ? path : [...path, humanize(key)])
      })
    }
  }

  walk(data, [])
  return lines
}

/**
 * @param {object} params
 * @param {number} params.status - the HTTP status of the rejected save
 * @param {*} params.data - its parsed body, if any
 * @param {string} params.itemKind - the editor's `kind`
 * @return {{ kind: string, status: number, messages: string[] }}
 */
export const describeSaveError = ({ status, data, itemKind }) => {
  const messages = flattenErrorMessages(data)

  let kind = SAVE_ERROR_KINDS.OTHER
  if (status === 400) {
    kind = SAVE_ERROR_KINDS.VALIDATION
  } else if (status === 404) {
    kind = SAVE_ERROR_KINDS.GONE
  } else if (status === 403 && itemKind === ITEM_TYPE_RESPONDER) {
    kind = SAVE_ERROR_KINDS.AUTORESPONDER_RUNNING
  } else if (status === 403 && PLAN_DETAIL_PATTERN.test(messages.join(' '))) {
    kind = SAVE_ERROR_KINDS.PLAN
  }

  return { kind, status, messages }
}
